<template>
  <header class="main-header">

    <!-- Decorative glow -->
    <div class="header-glow header-glow-one"></div>
    <div class="header-glow header-glow-two"></div>

    <div class="container">

      <nav class="navbar-custom">

        <!-- =========================
             LOGO
        ========================== -->

        <router-link
          :to="
            isSeller
              ? '/seller'
              : '/buyer'
          "
          class="brand"
        >
          <span class="brand-icon">
            📚
          </span>

          <span class="brand-text">
            BookStore
          </span>
        </router-link>


        <!-- =========================
             NAVIGATION
        ========================== -->

        <div class="nav-area">

          <router-link
            :to="
              isSeller
                ? '/seller'
                : '/buyer'
            "
            class="nav-item"
          >
            <span class="nav-icon">
              ◉
            </span>

            Home
          </router-link>


          <router-link
            to="/books"
            class="nav-item"
          >
            <span class="nav-icon">
              📖
            </span>

            Books
          </router-link>


          <!-- Wishlist -->
          <router-link
            v-if="isBuyer"
            to="/wishlist"
            class="nav-item"
          >

            <span class="nav-icon">
              ♡
            </span>

            Wishlist

            <span class="wishlist-counter">
              {{ wishlistCount }}
            </span>

          </router-link>


          <!-- Cart -->
          <router-link
            v-if="isBuyer"
            to="/cart"
            class="nav-item cart-link"
          >

            <span class="nav-icon">
              🛒
            </span>

            Cart

            <span class="cart-counter">
              {{ cartCount }}
            </span>

          </router-link>


          <!-- Seller Add Book -->
          <router-link
            v-if="isSeller"
            to="/books/add"
            class="add-book-btn"
          >
            + Add Book
          </router-link>


          <!-- Dark Mode -->
          <button
            class="theme-btn"
            @click="toggleDarkMode"
            :title="
              darkMode
                ? 'Switch to light mode'
                : 'Switch to dark mode'
            "
          >
            <span
              v-if="darkMode"
            >
              ☀️
            </span>

            <span v-else>
              🌙
            </span>

          </button>


          <!-- User -->
          <div class="user-area">

            <div class="avatar">
              {{ userInitial }}
            </div>

            <div class="user-info">

              <strong>
                {{ userName }}
              </strong>

              <span>
                {{
                  isSeller
                    ? 'Seller'
                    : 'Buyer'
                }}
              </span>

            </div>

            <button
              class="logout-btn"
              @click="logout"
            >
              Logout
            </button>

          </div>

        </div>

      </nav>

    </div>

  </header>
</template>

<script>
export default {
  name: 'Header',

  computed: {

    user() {
      return this.$store
        .getters.currentUser
    },

    userName() {
      return (
        this.user?.name ||
        'User'
      )
    },

    userInitial() {
      return this.userName
        .charAt(0)
        .toUpperCase()
    },

    isBuyer() {
      return this.$store
        .getters.isBuyer
    },

    isSeller() {
      return this.$store
        .getters.isSeller
    },

    wishlistCount() {
      return this.$store
        .getters.wishlistCount
    },

    cartCount() {
      return this.$store
        .getters.cartCount
    },

    darkMode() {
      return this.$store
        .state.darkMode
    }
  },

  methods: {

    toggleDarkMode() {
      this.$store.dispatch(
        'toggleDarkMode'
      )
    },

    logout() {
      this.$store.dispatch(
        'logout'
      )

      this.$router.push(
        '/auth'
      )
    }
  }
}
</script>

<style scoped>

/* =====================================
   HEADER
===================================== */

.main-header {
  position: sticky;

  top: 0;

  z-index: 1000;

  background:
    linear-gradient(
      135deg,
      #8543a4 0%,
      #9a6da9ee 50%,
      #de97e9ef 100%
    );

  border-bottom:
    1px solid #e7def7;

  box-shadow:
    0 8px 30px
    rgba(
      108,
      59,
      255,
      0.10
    );

  overflow: hidden;
}


/* =====================================
   DECORATIVE GLOW
===================================== */

.header-glow {
  position: absolute;

  border-radius: 50%;

  pointer-events: none;

  filter: blur(5px);
}

.header-glow-one {
  width: 180px;
  height: 180px;

  top: -120px;
  left: 25%;

  background:
    rgba(
      139,
      92,
      246,
      0.11
    );
}

.header-glow-two {
  width: 150px;
  height: 150px;

  right: 20%;
  bottom: -120px;

  background:
    rgba(
      236,
      72,
      153,
      0.08
    );
}


/* =====================================
   NAVBAR
===================================== */

.navbar-custom {
  position: relative;

  z-index: 2;

  min-height:
    78px;

  display:
    flex;

  justify-content:
    space-between;

  align-items:
    center;

  gap:
    25px;
}


/* =====================================
   BRAND
===================================== */

.brand {
  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  text-decoration:
    none;

  color:
    #291844;

  font-size:
    1.45rem;

  font-weight:
    800;

  letter-spacing:
    -0.5px;
}

.brand:hover {
  color:
    #6c3bff;
}

.brand-icon {
  width:
    45px;

  height:
    45px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    13px;

  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #8b5cf6
    );

  box-shadow:
    0 7px 18px
    rgba(
      108,
      59,
      255,
      0.25
    );

  font-size:
    1.35rem;
}

.brand-text {
  background:
    linear-gradient(
      135deg,
      #321566,
      #6c3bff
    );

  -webkit-background-clip:
    text;

  -webkit-text-fill-color:
    transparent;

  background-clip:
    text;
}


/* =====================================
   NAV AREA
===================================== */

.nav-area {
  display:
    flex;

  align-items:
    center;

  flex-wrap:
    wrap;

  gap:
    6px;
}


/* =====================================
   NAV ITEMS
===================================== */

.nav-item {
  position:
    relative;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    5px;

  padding:
    9px 12px;

  border-radius:
    10px;

  color:
    #5c526b;

  text-decoration:
    none;

  font-size:
    0.92rem;

  font-weight:
    600;

  transition:
    all 0.2s ease;
}

.nav-item:hover {
  color:
    #6c3bff;

  background:
    #eee8ff;

  transform:
    translateY(-1px);
}

.nav-item.router-link-exact-active {
  color:
    #6c3bff;

  background:
    #eee8ff;
}

.nav-icon {
  font-size:
    0.9rem;
}


/* =====================================
   WISHLIST COUNTER
===================================== */

.wishlist-counter {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  min-width:
    21px;

  height:
    21px;

  padding:
    0 6px;

  border-radius:
    50px;

  background:
    #eee7ff;

  color:
    #6c3bff;

  font-size:
    0.68rem;

  font-weight:
    800;
}


/* =====================================
   CART COUNTER
===================================== */

.cart-link {
  position:
    relative;
}

.cart-counter {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  min-width:
    22px;

  height:
    22px;

  padding:
    0 6px;

  border-radius:
    50px;

  background:
    linear-gradient(
      135deg,
      #ec4899,
      #db2777
    );

  color:
    #ffffff;

  font-size:
    0.68rem;

  font-weight:
    800;

  box-shadow:
    0 3px 8px
    rgba(
      236,
      72,
      153,
      0.25
    );
}


/* =====================================
   ADD BOOK
===================================== */

.add-book-btn {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    9px 16px;

  border-radius:
    10px;

  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #8b5cf6
    );

  color:
    #ffffff;

  text-decoration:
    none;

  font-size:
    0.88rem;

  font-weight:
    700;

  box-shadow:
    0 7px 16px
    rgba(
      108,
      59,
      255,
      0.20
    );
}

.add-book-btn:hover {
  color:
    #ffffff;

  transform:
    translateY(-2px);

  box-shadow:
    0 10px 22px
    rgba(
      108,
      59,
      255,
      0.28
    );
}


/* =====================================
   THEME BUTTON
===================================== */

.theme-btn {
  width:
    40px;

  height:
    40px;

  display:
    flex;

  justify-content:
    center;

  align-items:
    center;

  border:
    1px solid #ddd2ee;

  border-radius:
    50%;

  background:
    #ffffff;

  color:
    #6c3bff;

  font-size:
    1rem;

  cursor:
    pointer;

  box-shadow:
    0 4px 12px
    rgba(
      108,
      59,
      255,
      0.08
    );
}

.theme-btn:hover {
  background:
    #f2edff;

  transform:
    rotate(10deg);

  border-color:
    #b9a3e8;
}


/* =====================================
   USER AREA
===================================== */

.user-area {
  display:
    flex;

  align-items:
    center;

  gap:
    9px;

  margin-left:
    6px;

  padding-left:
    12px;

  border-left:
    1px solid #dfd5eb;
}


/* Avatar */

.avatar {
  width:
    37px;

  height:
    37px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    50%;

  background:
    linear-gradient(
      135deg,
      #6c3bff,
      #ec4899
    );

  color:
    #ffffff;

  font-size:
    0.9rem;

  font-weight:
    800;

  box-shadow:
    0 5px 12px
    rgba(
      108,
      59,
      255,
      0.20
    );
}


/* User info */

.user-info {
  display:
    flex;

  flex-direction:
    column;

  gap:
    2px;

  line-height:
    1.1;
}

.user-info strong {
  color:
    #2c1a43;

  font-size:
    0.82rem;

  font-weight:
    800;
}

.user-info span {
  color:
    #7c3aed;

  font-size:
    0.66rem;

  font-weight:
    700;
}


/* Logout */

.logout-btn {
  border:
    1px solid
    #f0bfd3;

  background:
    #fff4f8;

  color:
    #c02661;

  border-radius:
    9px;

  padding:
    7px 11px;

  font-size:
    0.77rem;

  font-weight:
    600;

  cursor:
    pointer;
}

.logout-btn:hover {
  background:
    #c02661;

  border-color:
    #c02661;

  color:
    #ffffff;
}


/* =====================================
   DARK MODE
===================================== */

:global(.dark-mode)
.main-header {
  background:
    linear-gradient(
      135deg,
      #a804ad 0%,
      #1b102d 50%,
      #211233 100%
    );

  border-bottom:
    1px solid #352445;

  box-shadow:
    0 8px 30px
    rgba(
      0,
      0,
      0,
      0.35
    );
}

:global(.dark-mode)
.brand-text {
  background:
    linear-gradient(
      135deg,
      #c4b5fd,
      #e9a8ff
    );

  -webkit-background-clip:
    text;

  -webkit-text-fill-color:
    transparent;

  background-clip:
    text;
}

:global(.dark-mode)
.nav-item {
  color:
    #c4bbd2;
}

:global(.dark-mode)
.nav-item:hover,
:global(.dark-mode)
.nav-item.router-link-exact-active {
  color:
    #c4b5fd;

  background:
    #2a1b40;
}

:global(.dark-mode)
.wishlist-counter {
  background:
    #2b1d43;

  color:
    #c4b5fd;
}

:global(.dark-mode)
.theme-btn {
  background:
    #251936;

  border-color:
    #4a3a5e;

  color:
    #c4b5fd;
}

:global(.dark-mode)
.theme-btn:hover {
  background:
    #302047;

  border-color:
    #644d7d;
}

:global(.dark-mode)
.user-area {
  border-left-color:
    #3b2b4a;
}

:global(.dark-mode)
.user-info strong {
  color:
    #f5f3ff;
}

:global(.dark-mode)
.user-info span {
  color:
    #c4b5fd;
}

:global(.dark-mode)
.logout-btn {
  background:
    #351a28;

  border-color:
    #692d4b;

  color:
    #f9a8d4;
}

:global(.dark-mode)
.logout-btn:hover {
  background:
    #c02661;

  color:
    #ffffff;
}


/* =====================================
   RESPONSIVE
===================================== */

@media (max-width: 1050px) {

  .navbar-custom {
    flex-direction:
      column;

    align-items:
      stretch;

    padding:
      15px 0;
  }

  .brand {
    justify-content:
      center;
  }

  .nav-area {
    justify-content:
      center;
  }

  .user-area {
    border-left:
      none;

    border-top:
      1px solid #dfd5eb;

    margin-left:
      0;

    padding-left:
      0;

    padding-top:
      10px;

    justify-content:
      center;
  }

  :global(.dark-mode)
  .user-area {
    border-top-color:
      #3b2b4a;
  }
}

@media (max-width: 576px) {

  .nav-area {
    gap:
      4px;
  }

  .nav-item {
    padding:
      7px 8px;

    font-size:
      0.82rem;
  }

  .user-info {
    display:
      none;
  }

  .user-area {
    gap:
      8px;
  }

  .brand {
    font-size:
      1.3rem;
  }
}
</style>